import { useEffect, useState } from "react";
import type { FC } from "react";
import { Image, type ModalProps, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useWeeklyChallengeModalLocalStorage } from "./useWeeklyChallengeModalLocalStorage";
import { useGetThemeQuery } from "api/generated";
import { Loading } from "components";
import { Lightbox } from "components/Lightbox";
import { processApiError } from "utils/processApiError";
import { useUserSubjects } from "utils/hooks/useUserSubjects";
import type { ThemeFragment } from "api/generated";

interface Props extends ModalProps {
  themeId: string;
  tutoringId: string;
  onClose: () => void;
}

export const WeeklyChallengeModal: FC<Props> = ({ themeId, tutoringId, onClose, ...modalProps }) => {
  const navigate = useNavigate();
  const { setSeenModalToLocalStorage, isConfirmationModalAllowed } = useWeeklyChallengeModalLocalStorage(themeId);
  const { data, isLoading, error: apiError } = useGetThemeQuery({ themeId });

  const [theme, setTheme] = useState<ThemeFragment | undefined>(undefined);
  const { userSubjects } = useUserSubjects();

  useEffect(() => {
    setSeenModalToLocalStorage();
  }, []);

  useEffect(() => {
    if (apiError === undefined) return;

    processApiError(apiError);
  }, [apiError]);

  useEffect(() => {
    if (!data) return;

    const theme = data.themes.at(0);
    setTheme(theme);
  }, [data]);

  if (!isConfirmationModalAllowed) return null;

  const onConfirm = () => {
    const subjectId = theme?.subject;
    if (!subjectId || !userSubjects) {
      navigate(`/doucovani`);
      return;
    }

    let subjectSlug: string | null | undefined = null;
    for (const userSubject of userSubjects) {
      if (userSubject.subject?.id === subjectId) subjectSlug = userSubject.subject.slug;
    }

    navigate(subjectSlug ? `/doucovani/${subjectSlug}/${tutoringId}` : `/doucovani`);
  };

  const handleClose = () => {
    onClose();
  };

  if (isLoading) return <Loading />;

  return (
    // Nadpis je pod ilustrací lektorky, takže zůstává v těle a jen pojmenuje dialog. [TD-344]
    <Lightbox {...modalProps} className="mt-5" onHide={handleClose} show={true} bodyTitleId="week-challenge-title">
      <div className="modal-week-challenge">
        <div className="d-flex justify-content-center">
          <Image src={"/images/teacher-bara.svg"} loading={"eager"} />
        </div>
        <div>
          {/* Velikost drží `.fs-16`, ať se sjednocením na `h2` (globálně 17 px) nezmění
              vzhled — původní `h4` měl 16 px ($h4-font-size). [TD-344] */}
          <h2 id="week-challenge-title" className="text-center fs-16">
            Fokus týdne - doučko
          </h2>
          <div className="text-center mb-1">
            {theme?.appName && (
              <>
                Téma na tento týden: <strong>{theme.appName}</strong>.
              </>
            )}
          </div>
          <div className="text-center mb-1">
            Připoj se k studentům, kteří se už pustili do doučování a chtějí být o krok napřed.
          </div>
          <div className="text-center mb-5">Každý týden nové téma, každý týden o krok blíž k jistotě u přijímaček.</div>
          <Button className="w-100" onClick={onConfirm}>
            Přejít na doučko
          </Button>
        </div>
      </div>
    </Lightbox>
  );
};
